import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Run } from '../types';
import { goalService } from './goalService';
import { Capacitor } from '@capacitor/core';
import { firestoreRestApi } from '../utils/firestoreRestApi';

export const runService = {
  // Create a new run
  async createRun(runData: Omit<Run, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, 'runs'), {
      ...runData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    // Update goal progress after creating a run
    try {
      // Get all goals for this athlete and update their progress
      const goals = await goalService.getGoalsByAthlete(runData.athleteId);
      for (const goal of goals) {
        await goalService.updateGoalProgress(goal.id);
      }
    } catch (error) {
      console.warn('Failed to update goal progress after run creation:', error);
    }
    
    return docRef.id;
  },

  // Get all runs for an athlete
  async getRunsByAthlete(athleteId: string): Promise<Run[]> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'runs',
          [{ field: 'athleteId', op: '==', value: athleteId }]
        );
        
        // Sort by createdAt descending (client-side)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().createdAt?.seconds || 0;
          const bTime = b.data().createdAt?.seconds || 0;
          return bTime - aTime;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt || Timestamp.now(),
          updatedAt: doc.data().updatedAt || Timestamp.now(),
        })) as Run[];
      } catch (error) {
        console.error('Error fetching runs via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    const q = query(
      collection(db, 'runs'),
      where('athleteId', '==', athleteId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt,
      updatedAt: doc.data().updatedAt,
    })) as Run[];
  },

  // Get runs for a specific team
  async getRunsByTeam(teamId: string): Promise<Run[]> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'runs',
          [{ field: 'teamId', op: '==', value: teamId }]
        );
        
        // Sort by createdAt descending (client-side)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().createdAt?.seconds || 0;
          const bTime = b.data().createdAt?.seconds || 0;
          return bTime - aTime;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt || Timestamp.now(),
          updatedAt: doc.data().updatedAt || Timestamp.now(),
        })) as Run[];
      } catch (error) {
        console.error('Error fetching team runs via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    const q = query(
      collection(db, 'runs'),
      where('teamId', '==', teamId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt,
      updatedAt: doc.data().updatedAt,
    })) as Run[];
  },

  // Get runs for an athlete in a specific team
  async getRunsByAthleteAndTeam(athleteId: string, teamId: string): Promise<Run[]> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'runs',
          [
            { field: 'athleteId', op: '==', value: athleteId },
            { field: 'teamId', op: '==', value: teamId }
          ]
        );
        
        // Sort by createdAt descending (client-side)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().createdAt?.seconds || 0;
          const bTime = b.data().createdAt?.seconds || 0;
          return bTime - aTime;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt || Timestamp.now(),
          updatedAt: doc.data().updatedAt || Timestamp.now(),
        })) as Run[];
      } catch (error) {
        console.error('Error fetching athlete/team runs via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    const q = query(
      collection(db, 'runs'),
      where('athleteId', '==', athleteId),
      where('teamId', '==', teamId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt,
      updatedAt: doc.data().updatedAt,
    })) as Run[];
  },

  // Get run by ID
  async getRunById(runId: string): Promise<Run | null> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const runDoc = await firestoreRestApi.getDocument('runs', runId);
        if (!runDoc) return null;
        
        return {
          id: runDoc.id,
          ...runDoc.data(),
          createdAt: runDoc.data().createdAt || Timestamp.now(),
          updatedAt: runDoc.data().updatedAt || Timestamp.now(),
        } as Run;
      } catch (error) {
        console.error('Error fetching run via REST API:', error);
        return null;
      }
    }
    
    // On web/Android, use SDK
    const runDoc = await getDoc(doc(db, 'runs', runId));
    if (!runDoc.exists()) return null;
    
    return {
      id: runDoc.id,
      ...runDoc.data(),
      createdAt: runDoc.data().createdAt,
      updatedAt: runDoc.data().updatedAt,
    } as Run;
  },

  // Update a run
  async updateRun(runId: string, updates: Partial<Omit<Run, 'id' | 'createdAt' | 'updatedAt' | 'athleteId' | 'teamId'>>): Promise<void> {
    const runRef = doc(db, 'runs', runId);
    await updateDoc(runRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
    
    // Update goal progress after updating a run
    try {
      const run = await this.getRunById(runId);
      if (run) {
        const goals = await goalService.getGoalsByAthlete(run.athleteId);
        for (const goal of goals) {
          await goalService.updateGoalProgress(goal.id);
        }
      }
    } catch (error) {
      console.warn('Failed to update goal progress after run update:', error);
    }
  },

  // Delete a run
  async deleteRun(runId: string): Promise<void> {
    // Get the run before deleting to update goal progress
    const run = await this.getRunById(runId);
    
    await deleteDoc(doc(db, 'runs', runId));
    
    // Update goal progress after deleting a run
    try {
      if (run) {
        const goals = await goalService.getGoalsByAthlete(run.athleteId);
        for (const goal of goals) {
          await goalService.updateGoalProgress(goal.id);
        }
      }
    } catch (error) {
      console.warn('Failed to update goal progress after run deletion:', error);
    }
  },

  // Get runs within a date range
  async getRunsByDateRange(athleteId: string, startDate: Date, endDate: Date): Promise<Run[]> {
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);
    
    const q = query(
      collection(db, 'runs'),
      where('athleteId', '==', athleteId),
      where('createdAt', '>=', startTimestamp),
      where('createdAt', '<=', endTimestamp),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt,
      updatedAt: doc.data().updatedAt,
    })) as Run[];
  },

  // Calculate pace from distance and time
  calculatePace(distance: number, timeInMinutes: number): string {
    if (distance === 0 || timeInMinutes === 0) return '0:00';
    
    const pacePerMile = timeInMinutes / distance;
    const minutes = Math.floor(pacePerMile);
    const seconds = Math.round((pacePerMile - minutes) * 60);
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  },

  // Format time from minutes to HH:MM:SS
  formatTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    const secs = Math.round((minutes % 1) * 60);
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  },

  // Convert time string (HH:MM:SS or MM:SS) to minutes
  parseTimeToMinutes(timeString: string): number {
    const parts = timeString.split(':').map(Number);
    
    if (parts.length === 3) {
      // HH:MM:SS format
      return parts[0] * 60 + parts[1] + parts[2] / 60;
    } else if (parts.length === 2) {
      // MM:SS format
      return parts[0] + parts[1] / 60;
    } else {
      // Just minutes
      return parts[0];
    }
  }
};
